import { Award, Gem, Heart } from 'lucide-react';

interface Feature {
  icon: any;
  title: string;
  description: string;
}

const defaultFeatures: Feature[] = [
  { icon: Gem, title: 'Piezas Exclusivas', description: 'Cada joya y fragancia es seleccionada cuidadosamente para garantizar exclusividad y calidad superior.' },
  { icon: Award, title: 'Tradición Artesanal', description: 'Trabajamos con artesanos expertos que mantienen vivas las técnicas tradicionales de la alta joyería.' },
  { icon: Heart, title: 'Atención Personalizada', description: 'Nuestro equipo te guía para encontrar la pieza perfecta que refleje tu estilo y personalidad.' },
];

interface AboutSectionProps {
  title?: string;
  subtitle?: string;
  paragraphs?: string[];
  features?: Feature[];
}

const AboutSection = ({ title, subtitle, paragraphs, features }: AboutSectionProps) => {
  const useFeatures = features ?? defaultFeatures;
  return (
    <section id="sobre" className="py-24 lg:py-32 bg-onyx-light relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Text Content */}
          <div>
            <span className="font-body text-sm uppercase tracking-[0.3em] text-primary mb-4 block">
              {title ?? 'Nuestra Historia'}
            </span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground mb-6">
              {subtitle ?? 'Sobre Tyche'}
            </h2>
            <div className="line-gold w-24 mb-8" />
            
            <div className="space-y-6 font-body text-muted-foreground leading-relaxed">
              {(paragraphs ?? [
                'Tyche nace de la pasión por lo extraordinario. Inspirados en la diosa griega de la fortuna y la prosperidad, creamos un espacio donde el lujo se encuentra con la atención al detalle más exquisita.',
                'Nuestra filosofía es simple: cada cliente merece sentirse único. Por eso, seleccionamos fragancias de casas perfumistas reconocidas mundialmente y piezas de joyería que cuentan historias de elegancia atemporal.',
                'En Tyche, no vendemos productos; creamos experiencias que perduran en la memoria y se transmiten de generación en generación.'
              ]).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>

          {/* Features */}
          <div className="space-y-6">
            <h3 className="font-display text-2xl font-semibold text-foreground mb-8">
              ¿Por qué elegir Tyche?
            </h3>
            
            {useFeatures.map((feature, index) => (
              <div
                key={feature.title}
                className="flex gap-5 p-6 card-luxury rounded-lg"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h4 className="font-display text-lg font-semibold text-foreground mb-2">
                    {feature.title}
                  </h4>
                  <p className="font-body text-sm text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
